import React, { useEffect, useState, useMemo } from "react";
import { Card, Table, Row, Col, Tabs, Spin, Tag } from "antd";
import { useTranslation } from "react-i18next";
import SubscriptionsTable from "./SubscriptionsTable";
import TransactionsTable from "./TransactionsTable";
import WalletDetails from "./WalletDetails";
import { getSubscriptions } from "../../../../network/network";
import CurrencyFormatter from "../../../../components/CurrencyFormatter";

const CompanySubscriptionsNew = ({ company }) => {
  const [subscriptionData, setSubscriptionData] = useState({});
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [searchParams, setSearchParams] = useState([]);
  const [paginationData, setPaginationData] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPageSize, setPerPageSize] = useState(10);

  const { t } = useTranslation();

  // Fetch data
  useEffect(() => {
    setIsDataLoading(true);

    getSubscriptions({
      ...searchParams,
      page: currentPage,
      per_page: perPageSize,
      company_id: company.id,
    })
      .then((response) => {
        const data = response.data.data;
        setSubscriptionData(data || {});
        setPaginationData(
          data?.balance_deductions_pagination || {
            current: 1,
            pageSize: 15,
            total: 0,
          }
        );
      })
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [searchParams, currentPage, perPageSize, company]);

  // Memoize activeAddons to avoid ESLint warnings
  const activeAddons = useMemo(() => {
    return subscriptionData.active_subscriptions_addons || [];
  }, [subscriptionData.active_subscriptions_addons]);

  // Memoized billing total
  const totalBillingAmount = useMemo(() => {
    return activeAddons.reduce((total, { quantity, unit_price, is_trial }) => {
      if (is_trial) return total;
      if (quantity) return total + quantity * unit_price;
      return total + unit_price;
    }, 0);
  }, [activeAddons]);

  const billingDetailsTableColumns = [
    {
      title: "#",
      key: "index",
      render: (_, __, index) => index + 1,
    },
    {
      title: t("service_name"),
      render: (subscription) => (
        <>
          {subscription.name}
          <br />
          {subscription.is_trial && <Tag color="blue">{t("trial")}</Tag>}
        </>
      ),
    },
    {
      title: t("price"),
      render: (subscription) => (
        <>
          <CurrencyFormatter value={subscription.unit_price} /> /{" "}
          {subscription.quantity ? t("per_user") : t("unlimited")}
        </>
      ),
    },
    {
      title: t("type"),
      dataIndex: "subscription_addon_type",
    },
    {
      title: t("quantity"),
      dataIndex: "quantity",
      align: "center",
      render: (quantity) => (quantity ? quantity : "-"),
    },
    {
      title: t("total"),
      render: (subscription) => (
        <CurrencyFormatter
          value={
            subscription.quantity
              ? subscription.quantity * subscription.unit_price
              : subscription.unit_price
          }
        />
      ),
    },
  ];

  return (
    <>
      <Spin spinning={isDataLoading}>
        {/* Billing + Wallet */}
        <Row gutter={{ xs: 20, sm: 20, md: 20, lg: 20 }}>
          <Col xs={24} sm={24} md={24} lg={15} xl={15} xxl={15}>
            <div
              style={{
                textAlign: "center",
                color: "#ffffff",
                backgroundColor: "#6301f2",
                padding: "10px",
              }}
            >
              {t("billing_details")}
            </div>

            <Table
              columns={billingDetailsTableColumns}
              dataSource={activeAddons}
              rowKey="id"
              pagination={false}
              summary={() => (
                <Table.Summary.Row
                  style={{ backgroundColor: "#fafafa", fontWeight: "bold" }}
                >
                  <Table.Summary.Cell colSpan={5}>
                    {t("total")}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell>
                    <CurrencyFormatter value={totalBillingAmount} />
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </Col>

          <Col xs={24} sm={24} md={24} lg={9} xl={9} xxl={9}>
            <WalletDetails
              balance={subscriptionData.balance}
              totalBillingAmount={totalBillingAmount}
            />
          </Col>
        </Row>

        {/* Tabs (Subscriptions / Spending History) */}
        <Card style={{ marginTop: "20px" }}>
          <Tabs
            defaultActiveKey="subscriptions-table"
            type="card"
            centered
            items={[
              {
                key: "subscriptions-table",
                label: t("subscriptions_history"),
                children: (
                  <SubscriptionsTable
                    subscriptionsAddons={subscriptionData.subscriptions_addons}
                  />
                ),
              },
              {
                key: "transactions-table",
                label: t("spending_history"),
                children: (
                  <TransactionsTable
                    balanceDeductionsHistory={
                      subscriptionData.balance_deductions_history
                    }
                    setSearchParams={setSearchParams}
                    paginationData={paginationData}
                    setPaginationData={setPaginationData}
                    setCurrentPage={setCurrentPage}
                    setPerPageSize={setPerPageSize}
                  />
                ),
              },
            ]}
          />
        </Card>
      </Spin>
    </>
  );
};

export default CompanySubscriptionsNew;
